import React, { useEffect, useRef, useState } from 'react';
import { Button, Select } from 'antd';
import { getNurseUnits, putNurseProfile } from '../../../api';
import { useToast } from '../../../ui/ToastProvider';

const MedicalUnitsStep = ({ onNext, onBack, initial, onSaved }) => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(initial?.units || []);
  const selectContainerRef = useRef(null);
  const { notify } = useToast();

  useEffect(() => {
    const loadUnits = async () => {
      try {
        const res = await getNurseUnits();
        setUnits(res.data || []);
      } catch (e) {
        notify({ title: 'Failed to load units', description: 'Please try again later.', type: 'error' });
      }
    };
    loadUnits();
  }, []);

  useEffect(() => {
    // Keep selection in sync if profile changes (e.g., after saving and navigating back)
    setSelected(initial?.units || []);
  }, [initial]);

  const handleSave = async () => {
    setLoading(true);
    try {
      if (!selected || selected.length === 0) {
        notify({ title: 'No units selected', description: 'Select at least one unit.', type: 'error' });
        setLoading(false);
        return;
      }
      await putNurseProfile({ units: selected });
      if (onSaved) onSaved({ units: selected });
      notify({ title: 'Units saved', description: 'Your medical units were updated.', type: 'success' });
      onNext();
    } catch (e) {
      notify({ title: 'Failed to save units', description: 'Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={selectContainerRef}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.2 }}>Medical units</div>
        <div style={{ color: '#667085' }}>Select up to 5 units you are qualified to work in.</div>
      </div>
      <style>{`
        .onboarding-select-lg .ant-select-selector {
          min-height: 60px !important;
          padding: 12px 12px !important;
        }
        .onboarding-select-lg .ant-select-selection-overflow {
          align-items: center;
        }
        /* Bigger option items in dropdown (scoped to popup) */
        .onboarding-select-lg-dropdown .ant-select-item {
          min-height: 56px !important;
        }
        .onboarding-select-lg-dropdown .ant-select-item-option {
          padding-top: 18px !important;
          padding-bottom: 18px !important;
        }
        .onboarding-select-lg-dropdown .ant-select-item-option-content {
          font-size: 18px !important;
          line-height: 28px !important;
        }
        /* Bigger selected tags text */
        .onboarding-select-lg .ant-select-selection-item {
          font-size: 16px !important;
        }
        /* Reduce autofill/password manager interference on webkit */
        .onboarding-select-lg input::-webkit-credentials-auto-fill-button {
          visibility: hidden;
          display: none !important;
        }
      `}</style>
      <Select
        mode="multiple"
        size="large"
        showSearch
        optionFilterProp="label"
        filterOption={(input, option) => {
          const label = (option?.label || '').toString().toLowerCase();
          return label.includes(input.toLowerCase());
        }}
        placeholder="Select units"
        className="onboarding-select-lg"
        popupClassName="onboarding-select-lg-dropdown"
        dropdownClassName="onboarding-select-lg-dropdown"
        style={{ width: '100%', marginBottom: 16 }}
        listHeight={360}
        dropdownStyle={{ maxHeight: 360 }}
        value={selected}
        onChange={(vals) => {
          if (vals.length > 5) return;
          setSelected(vals);
        }}
        onDropdownVisibleChange={() => {
          try {
            const root = selectContainerRef.current;
            if (!root) return;
            const inputEl = root.querySelector('.ant-select-selector input');
            if (inputEl) {
              inputEl.setAttribute('autocomplete', 'new-password');
              inputEl.setAttribute('autocapitalize', 'off');
              inputEl.setAttribute('autocorrect', 'off');
              inputEl.setAttribute('spellcheck', 'false');
              inputEl.setAttribute('name', 'units_search');
              inputEl.setAttribute('id', 'units_search');
            }
          } catch (e) {
            // ignore
          }
        }}
        options={(units || []).map((u) => ({ label: u, value: u }))}
      />
      <div style={{ marginTop: 32, paddingTop: 16, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {onBack ? (
          <Button htmlType="button" onClick={onBack} type="text" icon={<div style={{ marginRight: 6 }}>←</div>} style={{ color: '#667085', fontSize: 16 }}>Back</Button>
        ) : <div />}
        <Button
          type="primary"
          onClick={handleSave}
          loading={loading}
          style={{
            height: 48,
            padding: '0 32px',
            fontSize: 16,
            borderRadius: 8,
            backgroundColor: '#004d54',
            borderColor: '#004d54',
            boxShadow: '0 4px 12px rgba(0, 77, 84, 0.15)'
          }}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default MedicalUnitsStep;


